import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  Button,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  LockPerson as LockPersonIcon,
  AdminPanelSettings as AdminIcon,
  ContactSupport as SupportIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

interface UnauthorizedAccessProps {
  user?: any;
  reason?: string;
}

export const UnauthorizedAccess: React.FC<UnauthorizedAccessProps> = ({ 
  user, 
  reason = 'You need special permission to access the Control Panel.' 
}) => {
  const { logout } = useAuth();

  return (
    <Box 
      sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f8fafc',
        p: 2
      }}
    >
      <Container maxWidth="md">
        <Paper 
          elevation={3} 
          sx={{ 
            p: 5, 
            borderRadius: 3,
            textAlign: 'center',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
          }}
        >
          {/* Friendly Header */}
          <Box sx={{ mb: 4 }}>
            <LockPersonIcon 
              sx={{ 
                fontSize: 80, 
                color: 'primary.main',
                mb: 2,
                opacity: 0.8
              }} 
            />
            <Typography variant="h4" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              Almost There! 👋
            </Typography>
          </Box>

          {/* Main Message */}
          <Alert 
            severity="info" 
            sx={{ 
              mb: 4, 
              textAlign: 'left',
              backgroundColor: '#e3f2fd',
              borderRadius: 2,
              '& .MuiAlert-icon': {
                color: 'primary.main'
              }
            }}
          >
            <Typography variant="h6" component="div" gutterBottom sx={{ color: 'primary.main' }}>
              <AdminIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Control Panel Access Required
            </Typography>
            <Typography variant="body1" paragraph sx={{ color: 'text.primary' }}>
              The TalkForTy Control Panel is where users can create and organize their user specific settings and administrators 
              can update system level settings. Access is restricted to ensure the quality and safety of 
              communication tools that users depend on.
            </Typography>
            {reason && reason !== 'You need special permission to access the Control Panel.' && (
              <Typography variant="body2" sx={{ color: 'error.main', fontStyle: 'italic', mt: 1 }}>
                <strong>Reason:</strong> {reason}
              </Typography>
            )}
          </Alert>

          {/* User Info Card - More Welcoming */}
          {user && (
            <Card sx={{ mb: 4, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
              <CardContent sx={{ py: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Welcome, {user.displayName || user.firstName || 'User'}! 👤
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Email:</strong> {user.emailAddress || user.username || 'Not available'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    <strong>Current Role:</strong> Role {user.roleId}
                  </Typography>
                  {user.roleId !== undefined && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Access Level:</strong> {user.roleId} {user.roleId > 2 ? '(Needs Level 2 or lower for admin access)' : ''}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Action Buttons */}
          <Box sx={{ mb: 4 }}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={() => window.location.reload()}
              size="large"
              startIcon={<RefreshIcon />}
              sx={{ mr: 2, mb: { xs: 2, sm: 0 } }}
            >
              Try Again
            </Button>
            
            <Button 
              variant="outlined" 
              color="primary" 
              onClick={logout}
              size="large"
              startIcon={<LogoutIcon />}
            >
              Sign Out
            </Button>
          </Box>

          {/* Help Section */}
          <Box sx={{ mt: 4, p: 3, backgroundColor: '#f0f9ff', borderRadius: 2, border: '1px solid #bfdbfe' }}>
            <Typography variant="h6" gutterBottom color="primary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SupportIcon sx={{ mr: 1 }} />
              Need Help?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              If you believe you should have access to the Control Panel, here's what you can do:
            </Typography>
            <Box component="ul" sx={{ textAlign: 'left', color: 'text.secondary', fontSize: '0.875rem', pl: 3 }}>
              <li>Contact your team administrator or project manager</li>
              <li>Check if you're using the correct account</li>
              <li>Try refreshing the page if you just got access</li>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
